import React, { useState, useRef, useCallback, useEffect } from 'react';

const MAX_DURATION = 60;

export default function VoiceRecorder({ onRecordingComplete }) {
  const [state, setState] = useState('idle');
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startRecording = async () => {
    try {
      setErrorMsg('');
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        onRecordingComplete(blob);
        cleanup();
        setState('recorded');
      };
      recorder.start(1000);
      setState('recording');
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          if (prev >= MAX_DURATION - 1) { recorder.stop(); return MAX_DURATION; }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      setErrorMsg('Microphone access denied. Please allow mic permissions.');
      setState('error');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
  };

  const resetRecording = () => { cleanup(); onRecordingComplete(null); setState('idle'); setDuration(0); };
  const formatTime = (s) => { const m = Math.floor(s / 60); const sec = s % 60; return `${m}:${sec.toString().padStart(2, '0')}`; };

  return (
    <div className="rt-voice-recorder" data-testid="rt-voice-recorder">
      <label className="rt-form-label">🎙️ Voice Review <span className="rt-optional">(optional, maximum 60 seconds)</span></label>
      {state === 'idle' && (
        <button type="button" onClick={startRecording} className="rt-voice-btn rt-voice-start" data-testid="rt-voice-start">
          <span className="rt-mic-icon">🎤</span> Record Voice Review
        </button>
      )}
      {state === 'recording' && (
        <div className="rt-voice-recording">
          <div className="rt-recording-indicator">
            <span className="rt-pulse-dot" />
            <span className="rt-recording-text">Recording... {formatTime(duration)}</span>
            <span className="rt-recording-max">/ {formatTime(MAX_DURATION)}</span>
          </div>
          <div className="rt-voice-progress-bar">
            <div className="rt-voice-progress-fill" style={{ width: `${(duration / MAX_DURATION) * 100}%` }} />
          </div>
          <button type="button" onClick={stopRecording} className="rt-voice-btn rt-voice-stop" data-testid="rt-voice-stop">⏹ Stop Recording</button>
        </div>
      )}
      {state === 'recorded' && (
        <div className="rt-voice-done">
          <span className="rt-check-icon">✅</span>
          <span>Voice recorded ({formatTime(duration)})</span>
          <button type="button" onClick={resetRecording} className="rt-voice-btn rt-voice-redo" data-testid="rt-voice-redo">🔄 Re-record</button>
        </div>
      )}
      {state === 'error' && (
        <div className="rt-voice-error">
          <p>{errorMsg}</p>
          <button type="button" onClick={() => setState('idle')} className="rt-voice-btn">Try Again</button>
        </div>
      )}
    </div>
  );
}
